import type { App } from "@upsquad/server";
import type {
	ConfirmEmailChangePayload,
	MemberStatus,
	RequestEmailChangePayload,
	UpdateMemberRolePayload,
	UpdateProfilePayload,
} from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type GetActiveMembersResponse = App["~Routes"]["posts"][":communityId"]["active-members"]["get"]["response"]["200"];
type GetMyMembershipResponse = App["~Routes"]["posts"][":communityId"]["me"]["get"]["response"]["200"];
type GetMembersResponse = App["~Routes"]["member"][":communityId"]["get"]["response"]["200"];
type GetMemberDetailResponse = App["~Routes"]["member"][":communityId"][":userId"]["get"]["response"]["200"];
type UpdateMemberRoleResponse = App["~Routes"]["member"][":communityId"][":userId"]["role"]["patch"]["response"]["200"];
type RemoveMemberResponse = App["~Routes"]["community"][":communityId"]["members"][":userId"]["delete"]["response"]["200"];
type UpdateProfileResponse = App["~Routes"]["user"]["profile"]["patch"]["response"]["200"];
type RequestEmailChangeResponse = App["~Routes"]["user"]["email"]["request-change"]["post"]["response"]["200"];
type ConfirmEmailChangeResponse = App["~Routes"]["user"]["email"]["confirm-change"]["post"]["response"]["200"];

interface GetMembersParams {
	communityId: string;
	page?: number;
	limit?: number;
	search?: string;
	groupIds?: string[];
	status?: MemberStatus;
	includeInvited?: boolean;
}

interface GetMemberDetailParams {
	communityId: string;
	userId: string;
}

interface UpdateMemberRoleParams {
	communityId: string;
	userId: string;
	role: UpdateMemberRolePayload["role"];
}

interface RemoveMemberParams {
	communityId: string;
	userId: string;
}

const memberCoreApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getActiveMembers: builder.query<GetActiveMembersResponse, string>({
			query:
				(communityId): EdenCall<GetActiveMembersResponse> =>
				(client) =>
					client.posts({ communityId })["active-members"].get(),
			providesTags: [{ type: "Members", id: "active" }],
		}),
		getMyMembership: builder.query<GetMyMembershipResponse, string>({
			query:
				(communityId): EdenCall<GetMyMembershipResponse> =>
				(client) =>
					client.posts({ communityId }).me.get(),
			providesTags: (_result, _error, communityId) => [{ type: "Members", id: `me-${communityId}` }],
		}),
		getMembers: builder.query<GetMembersResponse, GetMembersParams>({
			query:
				({ communityId, page, limit, search, groupIds, status, includeInvited }): EdenCall<GetMembersResponse> =>
				(client) =>
					client.member({ communityId }).get({
						query: {
							page: page ?? 1,
							limit: limit ?? 10,
							search,
							group_ids: groupIds && groupIds.length > 0 ? groupIds.join(",") : undefined,
							status,
							include_invited: includeInvited ? true : undefined,
						},
					}),
			providesTags: ["Members"],
		}),
		getMemberDetail: builder.query<GetMemberDetailResponse, GetMemberDetailParams>({
			query:
				({ communityId, userId }): EdenCall<GetMemberDetailResponse> =>
				(client) =>
					client.member({ communityId })({ userId }).get(),
			providesTags: (_result, _error, { userId }) => [{ type: "MemberDetail", id: userId }],
		}),
		updateMemberRole: builder.mutation<UpdateMemberRoleResponse, UpdateMemberRoleParams>({
			query:
				({ communityId, userId, role }) =>
				(client) =>
					client.member({ communityId })({ userId }).role.patch({ role }),
			invalidatesTags: ["Members", "MemberDetail"],
		}),
		removeMember: builder.mutation<RemoveMemberResponse, RemoveMemberParams>({
			query:
				({ communityId, userId }) =>
				(client) =>
					client.community({ communityId }).members({ userId }).delete(),
			invalidatesTags: ["Members", "MemberDetail"],
		}),
		updateProfile: builder.mutation<UpdateProfileResponse, UpdateProfilePayload>({
			query:
				(payload): EdenCall<UpdateProfileResponse> =>
				(client) =>
					client.user.profile.patch(payload),
			invalidatesTags: ["MemberDetail"],
		}),
		requestEmailChange: builder.mutation<RequestEmailChangeResponse, RequestEmailChangePayload>({
			query:
				(payload): EdenCall<RequestEmailChangeResponse> =>
				(client) =>
					client.user.email["request-change"].post(payload),
		}),
		confirmEmailChange: builder.mutation<ConfirmEmailChangeResponse, ConfirmEmailChangePayload>({
			query:
				(payload): EdenCall<ConfirmEmailChangeResponse> =>
				(client) =>
					client.user.email["confirm-change"].post(payload),
			invalidatesTags: ["MemberDetail"],
		}),
	}),
});

export const {
	useGetActiveMembersQuery,
	useGetMyMembershipQuery,
	useGetMembersQuery,
	useGetMemberDetailQuery,
	useUpdateMemberRoleMutation,
	useRemoveMemberMutation,
	useUpdateProfileMutation,
	useRequestEmailChangeMutation,
	useConfirmEmailChangeMutation,
} = memberCoreApi;

export { type ApproveMemberAction, useApproveMemberMutation } from "./member-approval-api";
export {
	useAddUnavailableDateMutation,
	useDeleteUnavailableDateMutation,
	useGetAvailabilityQuery,
	useSetMyUnavailabilityMutation,
	useSetWeeklyScheduleMutation,
} from "./member-availability-api";
export { useGetCalendarConnectionsQuery, useUnlinkCalendarMutation } from "./member-calendar-api";
export {
	useCancelInvitationMutation,
	useGetInvitationsQuery,
	useResendInvitationMutation,
	useSendInvitationsMutation,
} from "./member-invitations-api";
export { useCreateSkillMutation, useGetSkillsQuery, useSetUserSkillsMutation } from "./member-skills-api";
